import { PUBLIC_API_PATH } from 'config';
import { request } from './client';

/*
 * Cart validation, shipping options, checkout, orders and payments: the
 * server's docs/PHASE2.md sections 4, 5 and 7. The server is always right
 * about money; these functions only carry the shopper's input and return the
 * `value` of the envelope.
 *
 * Order links are `/lacak/:token` and `/pembayaran/:token`; the token is the
 * only credential, so it is sent in the path and never logged.
 */
const base = PUBLIC_API_PATH;

const post = (url, data, options = {}) => request({ method: 'post', url: `${base}${url}`, data, ...options });
const get = (url, options = {}) => request({ method: 'get', url: `${base}${url}`, ...options });

/** Cart lines as the server wants them (the price shown is sent to flag a change). */
export const cartItems = (lines) =>
  lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity, unitPriceAmount: l.priceAmount }));

/**
 * POST /public/cart/validate -> { lines, pricing, coupon, shipping, issues }.
 * `shipping` is `{ optionId, quoteToken }` once the shopper chose a delivery
 * option (the server then prices it without asking the provider again).
 */
export async function validateCart({ lines, couponCode, shipping }, options) {
  const { value } = await post(
    '/cart/validate',
    {
      items: cartItems(lines),
      couponCode: couponCode || null,
      shippingOptionId: shipping ? shipping.optionId : null,
      quoteToken: shipping ? shipping.quoteToken : null,
    },
    options
  );
  return value;
}

let provincesPromise = null;

/** GET /public/provinces -> [{ code, name }] (the server caches it for a day). */
export function listProvinces() {
  if (!provincesPromise) {
    provincesPromise = get('/provinces')
      .then(({ value }) => value)
      .catch((error) => {
        provincesPromise = null;
        throw error;
      });
  }
  return provincesPromise;
}

/** POST /public/shipping/options. Without a destination: store pickup only. */
export async function fetchShippingOptions({ lines, destination }, options) {
  const { value } = await post(
    '/shipping/options',
    { items: lines.map((l) => ({ variantId: l.variantId, quantity: l.quantity })), destination: destination || null },
    options
  );
  return value;
}

/**
 * POST /public/checkout with an Idempotency-Key. Resolves with
 * `{ order: { number, accessToken, status, paymentStatus, totalAmount, expiresAt, ... }, payment }`.
 */
export async function placeOrder(payload, idempotencyKey, options = {}) {
  const { value } = await post('/checkout', payload, {
    ...options,
    headers: { ...(options.headers || {}), 'Idempotency-Key': idempotencyKey },
  });
  return value;
}

const orderPath = (token) => `/orders/${encodeURIComponent(token)}`;

/** GET /public/orders/:token -> the order as the shopper may see it. */
export async function fetchOrder(token, options) {
  const { value } = await get(orderPath(token), options);
  return value;
}

/** POST /public/orders/:token/cancel -> the order view (409 ORDER_NOT_CANCELLABLE once paid). */
export async function cancelOrder(token, options) {
  const { value } = await post(`${orderPath(token)}/cancel`, undefined, options);
  return value;
}

/**
 * POST /public/orders/:token/payments with an Idempotency-Key: reuses the
 * pending QR while it is valid, otherwise creates a new one.
 * With `replace: true` ("Ganti metode pembayaran") the shopper asks for a new
 * attempt even though the current one is still valid; the server closes the
 * old one (a Snap transaction is cancelled so its Virtual Account cannot be paid).
 * With `channelCode` (the method picked when the shopper bears a fee) the attempt is created for that
 * one method, with its fee as a separate surcharge; the server refuses a code that is not on offer.
 * `expectedPaymentAmount` is what the buyer saw for that method (the order's outstanding amount plus its fee): the
 * server answers `409 PAYMENT_TOTAL_CHANGED` with the fresh options when the fee is no longer the same (13.4).
 * Resolves with the payment (`{ attemptId, status, amount, surchargeAmount, instruction, expiresAt }`).
 */
export async function createPayment(token, idempotencyKey, { replace = false, channelCode = null, expectedPaymentAmount = null, ...options } = {}) {
  const body = {};
  if (replace) body.replace = true;
  if (channelCode) body.channelCode = channelCode;
  if (channelCode && Number.isInteger(expectedPaymentAmount)) body.expectedPaymentAmount = expectedPaymentAmount;
  const { value } = await post(`${orderPath(token)}/payments`, Object.keys(body).length ? body : undefined, {
    ...options,
    headers: { ...(options.headers || {}), 'Idempotency-Key': idempotencyKey },
  });
  return value;
}

/**
 * GET /public/orders/:token/payment-options -> [{ channel, channelCode, label, feeAmount, totalAmount }]:
 * the methods this order can be paid with and what each costs the shopper on top of the order. Only
 * asked for orders that are waiting for payment. (docs/MIDTRANS.md 11.3)
 */
export async function fetchPaymentOptions(token, options) {
  const { value } = await get(`${orderPath(token)}/payment-options`, options);
  return value;
}

const payPath = (token) => `/pay/${encodeURIComponent(token)}`;

/** GET /public/pay/:token -> the payment link's invoice view (docs/MIDTRANS.md 12.2); a bad link is one generic 404. */
export async function fetchPayLink(token, options) {
  const { value } = await get(payPath(token), options);
  return value;
}

/**
 * POST /public/pay/:token/payments { channelCode?, replace? } with an Idempotency-Key -> the attempt (redirect to
 * Snap). The amount is the one due now; the buyer never chooses it.
 */
export async function createLinkPayment(token, idempotencyKey, { replace = false, channelCode = null, expectedPaymentAmount = null, ...options } = {}) {
  const body = {};
  if (replace) body.replace = true;
  if (channelCode) body.channelCode = channelCode;
  if (channelCode && Number.isInteger(expectedPaymentAmount)) body.expectedPaymentAmount = expectedPaymentAmount;
  const { value } = await post(`${payPath(token)}/payments`, Object.keys(body).length ? body : undefined, {
    ...options,
    headers: { ...(options.headers || {}), 'Idempotency-Key': idempotencyKey },
  });
  return value;
}
